import { MatchEventFragment, TeamListItemFragment } from '@amf/shared/types/graphql';
import { MatchEventType } from '@amf/shared/data/Match';
import classNames from 'classnames';
import Link from 'next/link';
import { useTranslate } from '@tolgee/react';
import IconCircleArrowRight from '@amf/shared/icons/IconCircleArrowRight';

interface MatchEventItemProps {
  event: MatchEventFragment;
  playerPrefix: string;
  position?: 'left' | 'right';
  team?: TeamListItemFragment;
  offensePrefix?: string;
  facr?: boolean;
}

function MatchEventItem({
  event,
  playerPrefix,
  position: originalPosition,
  team,
  offensePrefix,
  facr,
}: MatchEventItemProps) {
  const { t } = useTranslate();
  const type = event.type as MatchEventType;
  const position = !event.isOwnGoal
    ? originalPosition
    : originalPosition === 'left'
    ? 'right'
    : 'left';

  return (
    <div
      className={classNames('MatchEvent', {
        'MatchEvent--goal':
          type === MatchEventType.goal &&
          (event.successGoal === true || event.successGoal === undefined),
        'MatchEvent--penalty': type === MatchEventType.penalty,
        'MatchEvent--red':
          type === MatchEventType.red ||
          (type === MatchEventType.yellow && event.redAfterYellowCard) ||
          (type === MatchEventType.goal && event.successGoal === false),
        'MatchEvent--yellow': type === MatchEventType.yellow && !event.redAfterYellowCard,
        'MatchEvent--right': position === 'right',
      })}
    >
      <div className='MatchEvent__minute'>
        {event.penaltyMethod ? (
          <>
            <i className='icons-soccer-goal' />
          </>
        ) : (
          <>{event.minute}′</>
        )}
      </div>
      <div className='MatchEvent__card'>
        <h4 className='MatchEvent__title'>
          {type === MatchEventType.goal && <i className='icons-soccer-goal' />}
          {type === MatchEventType.yellow && <i className='bi bi-file-fill' />}
          {type === MatchEventType.red && <i className='bi bi-file-fill' />}
          {type === MatchEventType.penalty && <i className='bi bi-exclamation-diamond-fill' />}
          <span>{event.title}</span>
        </h4>
        {event.message && (type === MatchEventType.goal || type === MatchEventType.penalty) && (
          <p className='MatchEvent__message'>{event.message}</p>
        )}
        <div className='MatchEvent__content'>
          {event.player && (
            <div className='MatchEvent__item'>
              <div className='MatchEvent__itemTitle'>
                {type === MatchEventType.goal &&
                  (event.successGoal === true || event.successGoal === undefined) &&
                  t('match.event.goal.player.title')}
                {type === MatchEventType.goal &&
                  event.successGoal === false &&
                  t('match.event.goal.player.title.penalty')}
                {type === MatchEventType.yellow && t('match.event.yellow.player.title')}
                {type === MatchEventType.red && t('match.event.red.player.title')}
                {type === MatchEventType.penalty && t('match.event.penalty.player.title')}
              </div>
              <div className='MatchEvent__itemValue'>
                <Link href={`${playerPrefix}/${event.player.id}`}>
                  <a>
                    {event.player.fullName} {event.player.facr && facr ? <>(FAČR) </> : null}
                    {event.player.currentTeam && (
                      <>({team?.name ?? event.player.currentTeam.name})</>
                    )}
                  </a>
                </Link>
              </div>
            </div>
          )}
          {!event.player && type === MatchEventType.goal && (
            <div className='MatchEvent__item'>
              <div className='MatchEvent__itemTitle'>
                {(event.successGoal === true || event.successGoal === undefined) &&
                  t('match.event.goal.player.title')}
                {event.successGoal === false && t('match.event.goal.player.title.penalty')}
              </div>
              <div className='MatchEvent__itemValue'>{event.teamName}</div>
            </div>
          )}
          {!event.penaltyMethod && (
            <>
              {event.assistant && (
                <div className='MatchEvent__item'>
                  <div className='MatchEvent__itemTitle'>{t('match.event.assist.title')}</div>
                  <div className='MatchEvent__itemValue'>
                    <Link href={`${playerPrefix}/${event.assistant.id}`}>
                      <a>
                        {event.assistant.fullName}{' '}
                        {event.assistant.currentTeam && (
                          <>({team?.name ?? event.assistant.currentTeam.name})</>
                        )}
                      </a>
                    </Link>
                  </div>
                </div>
              )}
              {!event.assistant && type === MatchEventType.goal && (
                <div className='MatchEvent__item'>
                  <div className='MatchEvent__itemTitle'>{t('match.event.assist.title')}</div>
                  <div className='MatchEvent__itemValue'>-</div>
                </div>
              )}
            </>
          )}

          {offensePrefix && event.offenseId && (
            <div className='MatchEvent__item'>
              <div className='MatchEvent__itemTitle'>{t('match.event.offense.title')}</div>
              <div className='MatchEvent__itemValue'>
                <Link href={`${offensePrefix}/${event.offenseId}`}>
                  <a style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {t('match.event.offense.link')} <IconCircleArrowRight />
                  </a>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MatchEventItem;
